import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Edit, Trash2, Eye, Search,
  X, Check, AlertCircle, Award, Medal, Crown,
  Star, Zap, Target, Flame, Brain, Heart,
  Shield, Sparkles, Gift,
  Users, BarChart3, Lock, Unlock,
  Calendar, Tag, Save, Upload, Loader2
} from 'lucide-react';
import { toast } from '@/components/ui/Toast';
import Modal from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import Pagination from '@/components/ui/Pagination';
import { FileUploadField } from '@/components/common/form-fields';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { isCleanText, CLEAN_TEXT_MESSAGE } from '@/utils/sanitize';
import { useDebounce } from '@/utils/debounce';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { BADGE_MANAGEMENT_PERMISSION } from '@/constants/constant-permissions';

// --- IMPORTS FROM SHARED TYPES (Efficient) ---
import { gamificationBadgeService } from '@/services/gamification.service';
import type {
  Badge,
  BadgeRarity,
  BadgeMetrics
} from '@/types/gamification.types';

// --- Validation Schema (Form Data) ---
// We keep this separate as forms often need specific validation states not in the DTO
// With yupResolver the Input/Textarea clean-text rule is skipped, so the schema keeps the check
const badgeSchema = yup.object({
  name: yup.string().required('Badge name is required')
    .test('clean-text', CLEAN_TEXT_MESSAGE, (v) => isCleanText(v)),
  description: yup.string().required('Description is required')
    .test('clean-text', CLEAN_TEXT_MESSAGE, (v) => isCleanText(v)),
  iconType: yup.string(),
  iconColor: yup.string(),
  rarity: yup.string()
    .oneOf(['common', 'rare', 'epic', 'legendary'], 'Invalid rarity selected')
    .required('Rarity is required'),
  xpRequired: yup.number()
    .typeError('XP must be a number')
    .required('XP required is required')
    .positive('XP must be greater than 0')
    .integer('XP must be a whole number'),
  category: yup.string().required('Category is required')
    .test('clean-text', CLEAN_TEXT_MESSAGE, (v) => isCleanText(v)),
  isActive: yup.boolean().default(true),
  image: yup.mixed().nullable().notRequired(),
});

type BadgeFormData = yup.InferType<typeof badgeSchema>;

const iconOptions = [
  { value: 'crown', label: 'Crown', icon: Crown },
  { value: 'medal', label: 'Medal', icon: Medal },
  { value: 'award', label: 'Award', icon: Award },
  { value: 'star', label: 'Star', icon: Star },
  { value: 'flame', label: 'Flame', icon: Flame },
  { value: 'lightning', label: 'Lightning', icon: Zap },
  { value: 'target', label: 'Target', icon: Target },
  { value: 'brain', label: 'Brain', icon: Brain },
  { value: 'heart', label: 'Heart', icon: Heart },
  { value: 'shield', label: 'Shield', icon: Shield },
  { value: 'gift', label: 'Gift', icon: Gift },
  { value: 'sparkles', label: 'Sparkles', icon: Sparkles },
];

const rarityOptions = [
  { value: 'common', label: 'Common' },
  { value: 'rare', label: 'Rare' },
  { value: 'epic', label: 'Epic' },
  { value: 'legendary', label: 'Legendary' },
];

const filterRarityOptions = [
  { value: 'all', label: 'All Rarities' },
  { value: 'common', label: 'Common' },
  { value: 'rare', label: 'Rare' },
  { value: 'epic', label: 'Epic' },
  { value: 'legendary', label: 'Legendary' },
];

const filterStatusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const sortByOptions = [
  { value: 'created', label: 'Sort by Date' },
  { value: 'name', label: 'Sort by Name' },
  { value: 'xp', label: 'Sort by XP' },
];

export default function BadgeManagement() {
  usePermissionRouteAccess([
    BADGE_MANAGEMENT_PERMISSION.ADMIN_BADGE_MANAGEMENT_READ,
    BADGE_MANAGEMENT_PERMISSION.ADMIN_BADGE_MANAGEMENT_WRITE,
  ]);
  const canWrite = useHasPermission([BADGE_MANAGEMENT_PERMISSION.ADMIN_BADGE_MANAGEMENT_WRITE]);

  const queryClient = useQueryClient();
  const [activeView, setActiveView] = useState<'list' | 'create'>('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [sortOrder, _setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [badgeImageUrl, setBadgeImageUrl] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Debounce search query (1 second delay)
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  // Filter form for reusable Select components
  const { control: filterControl, watch: watchFilter } = useForm({
    defaultValues: {
      filterRarity: 'all',
      filterStatus: 'all',
      sortBy: 'created',
    }
  });

  const filterRarity = watchFilter('filterRarity');
  const filterStatus = watchFilter('filterStatus');
  const sortBy = watchFilter('sortBy') as 'name' | 'xp' | 'created';

  // Reset to first page when filters change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const { register, handleSubmit, control, reset, watch, setValue, setError, formState: { errors } } = useForm<BadgeFormData>({
    resolver: yupResolver(badgeSchema) as any,
    // Validate while typing so the clean-text error shows under the field, not only after Save
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      iconType: 'crown',
      iconColor: '#f59e0b',
      rarity: 'common',
      xpRequired: undefined,
      category: '',
      isActive: true,
      image: undefined,
    }
  });

  const watchedName = watch('name');
  const watchedDescription = watch('description');
  const watchedIconType = watch('iconType');
  const watchedIconColor = watch('iconColor');
  const watchedRarity = watch('rarity') as BadgeRarity;

  // --- Queries ---

  const { data: badgesData, isLoading: isLoadingBadges } = useQuery({
    queryKey: ['badges', { searchTerm: debouncedSearchTerm, filterRarity, filterStatus, currentPage, itemsPerPage }],
    queryFn: async () => {
      const filter: any = {};
      if (debouncedSearchTerm) filter.search = debouncedSearchTerm;
      if (filterRarity !== 'all') filter.rarity = filterRarity as BadgeRarity;
      if (filterStatus === 'active') filter.is_active = true;
      if (filterStatus === 'inactive') filter.is_active = false;

      return gamificationBadgeService.listBadges({
        page: currentPage,
        limit: itemsPerPage,
        filter
      });
    }
  });

  const { data: metricsData } = useQuery({
    queryKey: ['badge-metrics'],
    queryFn: () => gamificationBadgeService.getMetrics(),
  });

  // Direct access to data from API type
  const badges: Badge[] = badgesData?.data || [];
  const totalItems = (badgesData as any)?.pagination?.total || badgesData?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  // Sorting
  const processedBadges = [...badges].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.badge_name.localeCompare(b.badge_name);
        break;
      case 'xp':
        comparison = a.xp_required - b.xp_required;
        break;
      case 'created':
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Use BadgeMetrics type directly
  const stats: BadgeMetrics = {
    total_badges: metricsData?.total_badges || 0,
    active_badges: metricsData?.active_badges || 0,
    inactive_badges: metricsData?.inactive_badges || 0,
    total_earned: metricsData?.total_earned || 0,
    average_xp_required: metricsData?.average_xp_required || 0,
  };

  // --- Mutations ---

  // Helper to extract error message from API response
  const getErrorMessage = (error: any, fallback: string): string => {
    // Check for API error (success: false) - message is directly on the error
    if (error?.isApiError && error?.message) {
      return error.message;
    }
    // Check for axios error response
    return error?.response?.data?.error?.error_description
      || error?.response?.data?.message
      || error?.message
      || fallback;
  };

  const createMutation = useMutation({
    mutationFn: (data: any) => gamificationBadgeService.createBadge(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['badges'] });
      queryClient.invalidateQueries({ queryKey: ['badge-metrics'] });
      toast.success('Badge created successfully!');
      resetForm();
      setActiveView('list');
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to create badge'));
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string, data: any }) => gamificationBadgeService.updateBadge(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['badges'] });
      queryClient.invalidateQueries({ queryKey: ['badge-metrics'] });
      toast.success('Badge updated successfully!');
      resetForm();
      setActiveView('list');
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update badge'));
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => gamificationBadgeService.deleteBadge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['badges'] });
      queryClient.invalidateQueries({ queryKey: ['badge-metrics'] });
      toast.success('Badge deleted successfully');
      setShowDeleteModal(false);
      setSelectedBadge(null);
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to delete badge'));
    }
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => gamificationBadgeService.toggleBadgeStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['badges'] });
      queryClient.invalidateQueries({ queryKey: ['badge-metrics'] });
      toast.success('Badge status updated');
      setShowToggleModal(false);
      setSelectedBadge(null);
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update status'));
    }
  });

  // Handlers
  const onSubmit = async (data: BadgeFormData) => {
    if (!badgeImageUrl) {
      if (!data.iconType) {
        setError('iconType', { message: 'Icon is required when no image is uploaded' });
        return;
      }
      if (!data.iconColor) {
        setError('iconColor', { message: 'Color scheme is required when no image is uploaded' });
        return;
      }
    }

    // Mapping Form Data (camelCase) to API DTO (snake_case)
    const apiPayload = {
      badge_name: data.name,
      description: data.description,
      icon_type: data.iconType as any,
      icon_color: data.iconColor,
      rarity: data.rarity as BadgeRarity,
      xp_required: data.xpRequired,
      category: data.category,
      is_active: data.isActive,
      badge_url: badgeImageUrl || '',  // API expects 'badge_url' for uploaded image URL
    };

    if (selectedBadge) {
      updateMutation.mutate({ id: getBadgeId(selectedBadge), data: apiPayload });
    } else {
      createMutation.mutate(apiPayload);
    }
  };

  const handleEdit = (badge: Badge) => {
    setSelectedBadge(badge);
    const existingImageUrl = getBadgeImageUrl(badge);
    setBadgeImageUrl(existingImageUrl || null);

    // Handle both snake_case and camelCase from API
    const iconType = (badge as any).icon_type || (badge as any).iconType || 'crown';
    const iconColor = (badge as any).icon_color || (badge as any).iconColor || '#f59e0b';

    reset({
      name: badge.badge_name,
      description: badge.description,
      iconType: iconType,
      iconColor: iconColor,
      rarity: badge.rarity,
      xpRequired: badge.xp ?? badge.xp_required,
      category: badge.category,
      isActive: badge.is_active,
      image: undefined,
    });
    setActiveView('create');
  };

  const resetForm = () => {
    reset({
      name: '',
      description: '',
      iconType: 'crown',
      iconColor: '#f59e0b',
      rarity: 'common',
      xpRequired: undefined,
      category: '',
      isActive: true,
      image: undefined,
    });
    setSelectedBadge(null);
    setBadgeImageUrl(null);
  };

  // Handle badge image change from FileUploadField
  const handleBadgeImageChange = (url: string | null) => {
    setBadgeImageUrl(url);
  };

  // Handle badge image removal
  const handleRemoveBadgeImage = () => {
    setBadgeImageUrl(null);
    toast.success('Badge image removed');
  };

  // --- Render Helpers ---

  // Helper to get badge ID (API may return _id or id)
  const getBadgeId = (badge: Badge): string => {
    return badge.id || badge._id || '';
  };

  // Helper to get badge XP (API may return xp or xp_required)
  const getBadgeXP = (badge: Badge): number => {
    return badge.xp ?? badge.xp_required ?? 0;
  };

  // Helper to get badge image URL (API returns 'icon' for uploaded image URL)
  // Only return if it's a valid URL (uploaded image)
  const getBadgeImageUrl = (badge: Badge): string | undefined => {
    const iconValue = badge.icon || badge.badge_url;
    // Check if it's a valid URL (starts with http/https) and not empty
    if (iconValue && iconValue.trim() !== '' && (iconValue.startsWith('http://') || iconValue.startsWith('https://'))) {
      return iconValue;
    }
    return undefined;
  };

  const getIconComponent = (iconType: string | undefined) => {
    const icon = iconOptions.find(i => i.value === iconType);
    return icon ? icon.icon : Crown;
  };

  const getRarityBadge = (rarity: string) => {
    const styles = {
      common: 'bg-gray-100 text-gray-700 border-gray-300',
      rare: 'bg-blue-100 text-blue-700 border-blue-300',
      epic: 'bg-purple-100 text-purple-700 border-purple-300',
      legendary: 'bg-amber-100 text-amber-700 border-amber-300',
    };
    return styles[rarity as keyof typeof styles] || styles.common;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-violet-700 rounded-2xl shadow-xl">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-purple-400/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-300/10 rounded-full blur-3xl" />
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        </div>

        {/* Header Content */}
        <div className="relative px-6 pt-6 pb-8 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-lg ring-1 ring-white/30">
                <Award className="w-8 h-8 text-white drop-shadow-md" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight drop-shadow-sm">
                  Badge Management
                </h1>
                <p className="text-indigo-100/90 mt-1 text-sm sm:text-base">
                  Create and manage badges to reward your learners
                </p>
              </div>
            </div>
            {activeView === 'list' && (
              <button
                onClick={() => {
                  if (!canWrite) return;
                  resetForm();
                  setActiveView('create');
                }}
                disabled={!canWrite}
                title={!canWrite ? 'You do not have permission' : undefined}
                className={`group flex items-center gap-2 px-5 py-2.5 bg-white text-indigo-600 rounded-xl transition-all font-semibold shadow-lg ${
                  canWrite ? 'hover:bg-indigo-50 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]' : 'opacity-50 cursor-not-allowed'
                }`}
              >
                <Plus className="w-5 h-5 transition-transform group-hover:rotate-90" />
                Create Badge
              </button>
            )}
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6">
            <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 border border-white/50">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gradient-to-br from-violet-100 to-purple-200 rounded-lg shadow-sm">
                  <Award className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_badges}</p>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Total Badges</p>
                </div>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 border border-white/50">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gradient-to-br from-emerald-100 to-teal-200 rounded-lg shadow-sm">
                  <Check className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.active_badges}</p>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Active Badges</p>
                </div>
              </div>
            </div>
<div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 border border-white/50">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gradient-to-br from-red-100 to-orange-200 rounded-lg shadow-sm">
                  <X className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.inactive_badges}</p>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Inactive Badges</p>
                </div>
              </div>
           </div>
            <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 border border-white/50">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gradient-to-br from-sky-100 to-cyan-200 rounded-lg shadow-sm">
                  <Users className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{(stats.total_earned ?? 0).toLocaleString()}</p>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Total Earned</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div>
        {activeView === 'list' ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            {/* Filters */}
            <div className="p-5 border-b border-gray-100">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search badges..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-gray-50"
                  />
                </div>
                <div className="flex gap-3 flex-wrap items-center">
                  <div className="w-40">
                    <Select
                      name="filterRarity"
                      control={filterControl}
                      options={filterRarityOptions}
                      isSearchable={false}
                      placeholder="All Rarities"
                    />
                  </div>
                  <div className="w-36">
                    <Select
                      name="filterStatus"
                      control={filterControl}
                      options={filterStatusOptions}
                      isSearchable={false}
                      placeholder="All Status"
                    />
                  </div>
                  <div className="w-40">
                    <Select
                      name="sortBy"
                      control={filterControl}
                      options={sortByOptions}
                      isSearchable={false}
                      placeholder="Sort by Date"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Badges List */}
            <div className="overflow-x-auto">
              {isLoadingBadges ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Badge</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Rarity</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">XP Required</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {processedBadges.map((badge) => {
                      const badgeImageUrl = getBadgeImageUrl(badge);
                      const badgeIconType = (badge as any).icon_type || (badge as any).iconType;
                      const badgeIconColor = (badge as any).icon_color || (badge as any).iconColor || '#f59e0b';
                      const IconComponent = getIconComponent(badgeIconType);
                      return (
                        <tr key={getBadgeId(badge)} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-4">
                              {badgeImageUrl ? (
                                <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-gray-100 shadow-sm">
                                  <img src={badgeImageUrl} alt={badge.badge_name} className="w-full h-full object-cover" />
                                </div>
                              ) : (
                                <div className="w-11 h-11 rounded-full flex items-center justify-center text-white shadow-sm" style={{ backgroundColor: badgeIconColor }}>
                                  <IconComponent className="w-5 h-5" />
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 truncate">{badge.badge_name}</p>
                                <p className="text-sm text-gray-500 truncate max-w-[200px]">{badge.description}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-3 py-1.5 rounded-full text-xs font-semibold capitalize ${getRarityBadge(badge.rarity)}`}>
                              {badge.rarity}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1">
                              <Zap className="w-4 h-4 text-amber-500" />
                              <span className="font-semibold text-gray-900">{getBadgeXP(badge).toLocaleString()}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                          <span className="inline-flex px-2.5 py-1 text-gray-700 rounded-md text-sm">{badge.category || "-"}</span>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => {
                                if (!canWrite) return;
                                setSelectedBadge(badge);
                                setShowToggleModal(true);
                              }}
                              disabled={!canWrite || toggleMutation.isPending}
                              title={!canWrite ? 'You do not have permission' : undefined}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                                !canWrite ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500' : badge.is_active
                                ? 'bg-green-50 text-green-700 hover:bg-green-100'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                              {badge.is_active ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                              {badge.is_active ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => {
                                  setSelectedBadge(badge);
                                  setShowPreviewModal(true);
                                }}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
                                title="Preview"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => canWrite && handleEdit(badge)}
                                disabled={!canWrite}
                                className="p-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                title={!canWrite ? 'You do not have permission' : 'Edit'}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  if (!canWrite) return;
                                  setSelectedBadge(badge);
                                  setShowDeleteModal(true);
                                }}
                                disabled={!canWrite}
                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                title={!canWrite ? 'You do not have permission' : 'Delete'}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {!isLoadingBadges && processedBadges.length > 0 && totalItems > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                showItemsPerPage={true}
              />
            )}

            {!isLoadingBadges && processedBadges.length === 0 && (
              <div className="p-12 text-center">
                <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">No badges found</p>
                <p className="text-gray-400 text-sm mt-1">Try adjusting your filters or create a new badge</p>
              </div>
            )}
          </div>
        ) : (
          /* Create/Edit Form */
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedBadge ? 'Edit Badge' : 'Create New Badge'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedBadge ? 'Update badge details below' : 'Fill in the details to create a new badge'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setActiveView('list');
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Badge Preview */}
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-100">
                  <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-4 text-center">Live Preview</p>
                  <div className="flex items-center justify-center">
                    <div className="text-center">
                      {badgeImageUrl ? (
                        <div className="w-20 h-20 mx-auto mb-3 rounded-full overflow-hidden border-4 border-white shadow-lg ring-4 ring-amber-100">
                          <img src={badgeImageUrl} alt="Preview" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-20 h-20 mx-auto mb-3 rounded-full flex items-center justify-center text-white shadow-lg ring-4 ring-amber-100" style={{ backgroundColor: watchedIconColor || '#f59e0b' }}>
                          {React.createElement(getIconComponent(watchedIconType || 'crown'), { className: 'w-10 h-10' })}
                        </div>
                      )}
                      <p className="font-bold text-gray-900">{watchedName || 'Badge Name'}</p>
                      <p className="text-sm text-gray-500 mt-1 max-w-xs">{watchedDescription || 'Badge description'}</p>
                      <span className={`inline-block mt-3 px-3 py-1 rounded-full text-xs font-semibold capitalize ${getRarityBadge(watchedRarity || 'common')}`}>
                        {watchedRarity || 'common'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Basic Information Section */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Award className="w-4 h-4 text-amber-500" />
                    Basic Information
                  </h3>
                  <div className="space-y-4">
                    <Input<BadgeFormData>
                      name="name"
                      control={control}
                      label="Badge Name"
                      required
                      type="text"
                      placeholder="Enter badge name"
                    />

                    <Textarea<BadgeFormData>
                      name="description"
                      control={control}
                      label="Description"
                      required
                      rows={3}
                      placeholder="Enter badge description"
                    />
                  </div>
                </div>

                {/* Appearance Section */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    Appearance
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Select
                        name="iconType"
                        control={control}
                        label="Icon"
                        options={iconOptions}
                        required
                        error={errors.iconType?.message}
                        isDisabled={!!badgeImageUrl}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Color Scheme <span className="text-red-500">*</span></label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={watchedIconColor || '#f59e0b'}
                          onChange={(e) => setValue('iconColor', e.target.value)}
                          className="w-full h-[42px] p-1 border border-gray-200 rounded-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={!!badgeImageUrl}
                        />
                      </div>
                      {errors.iconColor && <p className="text-red-500 text-xs mt-1">{errors.iconColor.message}</p>}
                    </div>
                  </div>

                  </div>

                {/* Badge Details Section */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />
                    Badge Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Select
                        name="rarity"
                        control={control}
                        label="Rarity"
                        options={rarityOptions}
                        required
                        error={errors.rarity?.message}
                      />
                    </div>

                    <Input<BadgeFormData>
                      name="xpRequired"
                      control={control}
                      label="XP Required"
                      required
                      type="number"
                      placeholder="0"
                    />

                    <Input<BadgeFormData>
                      name="category"
                      control={control}
                      label="Category"
                      required
                      type="text"
                      placeholder="Achievement"
                    />
                  </div>
                </div>

                {/* Image Upload Section */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Upload className="w-4 h-4 text-amber-500" />
                    Badge Image <span className="text-gray-400 font-normal text-xs">(Optional)</span>
                  </h3>
                  <FileUploadField
                    description="Upload a custom image for the badge. This will override the icon selection above."
                    currentFileUrl={badgeImageUrl || undefined}
                    onFileChange={handleBadgeImageChange}
                    onRemove={handleRemoveBadgeImage}
                    accept="image/*"
                    fileType="image"
                    maxSizeMB={2}
                    metadata={{ category: 'badge', type: 'badge-image' }}
                  />
                </div>

                {/* Status Toggle */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Active Status</p>
                        <p className="text-xs text-gray-500">Users can earn this badge when active</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        {...register('isActive')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    </label>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { resetForm(); setActiveView('list'); }}
                    className="px-5 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="px-5 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    {(createMutation.isPending || updateMutation.isPending) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {selectedBadge ? 'Update Badge' : 'Create Badge'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
        }
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal && !!selectedBadge}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Badge"
        subtitle="This action cannot be undone"
        size="md"
        footer={
          <div className="flex gap-3 w-full">
            <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold">Cancel</button>
            <button onClick={() => deleteMutation.mutate(getBadgeId(selectedBadge!))} disabled={deleteMutation.isPending} className="flex-1 px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold shadow-lg shadow-red-200 hover:shadow-xl hover:shadow-red-300 disabled:opacity-50 flex justify-center items-center gap-2">
              {deleteMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Delete Badge
            </button>
          </div>
        }
      >
        {selectedBadge && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex-shrink-0 mt-0.5"><div className="p-2 bg-red-100 rounded-full"><AlertCircle className="w-5 h-5 text-red-600" /></div></div>
              <div className="flex-1 space-y-1">
                <h4 className="font-semibold text-gray-900 text-base">Are you absolutely sure?</h4>
                <p className="text-gray-700 text-sm leading-relaxed">You are about to delete <span className="font-bold text-red-700">"{selectedBadge.badge_name}"</span></p>
              </div>
            </div>
            {/* Impact Summary - Stats from badge data not available in list, so removed dynamic user count for now */}
            <p className="text-xs text-gray-600 text-center px-4">This action is permanent and cannot be reversed.</p>
          </div>
        )}
      </Modal>

      {/* Toggle Status Confirmation Modal */}
      <Modal
        isOpen={showToggleModal && !!selectedBadge}
        onClose={() => setShowToggleModal(false)}
        title={selectedBadge?.is_active ? 'Deactivate Badge' : 'Activate Badge'}
        subtitle="Confirm status change"
        size="md"
        footer={
          <div className="flex gap-3 w-full">
            <button onClick={() => setShowToggleModal(false)} className="flex-1 px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold">Cancel</button>
            <button
              onClick={() => toggleMutation.mutate(getBadgeId(selectedBadge!))}
              disabled={toggleMutation.isPending}
              className={`flex-1 px-5 py-2.5 text-white rounded-lg transition-all font-semibold shadow-lg disabled:opacity-50 flex justify-center items-center gap-2 ${
                selectedBadge?.is_active
                  ? 'bg-orange-600 hover:bg-orange-700 shadow-orange-200 hover:shadow-xl hover:shadow-orange-300'
                  : 'bg-green-600 hover:bg-green-700 shadow-green-200 hover:shadow-xl hover:shadow-green-300'
              }`}
            >
              {toggleMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {selectedBadge?.is_active ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        }
      >
        {selectedBadge && (
          <div className="space-y-4">
            <div className={`flex items-start gap-3 p-3 border rounded-lg ${
              selectedBadge.is_active
                ? 'bg-orange-50 border-orange-200'
                : 'bg-green-50 border-green-200'
            }`}>
              <div className="flex-shrink-0 mt-0.5">
                <div className={`p-2 rounded-full ${selectedBadge.is_active ? 'bg-orange-100' : 'bg-green-100'}`}>
                  {selectedBadge.is_active
                    ? <X className="w-5 h-5 text-orange-600" />
                    : <Check className="w-5 h-5 text-green-600" />
                  }
                </div>
              </div>
              <div className="flex-1 space-y-1">
                <h4 className="font-semibold text-gray-900 text-base">
                  {selectedBadge.is_active ? 'Deactivate this badge?' : 'Activate this badge?'}
                </h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  You are about to {selectedBadge.is_active ? 'deactivate' : 'activate'}{' '}
                  <span className={`font-bold ${selectedBadge.is_active ? 'text-orange-700' : 'text-green-700'}`}>
                    "{selectedBadge.badge_name}"
                  </span>
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-600 text-center px-4">
              {selectedBadge.is_active
                ? 'Users will no longer be able to earn this badge while it is inactive.'
                : 'Users will be able to earn this badge once it is activated.'}
            </p>
          </div>
        )}
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={showPreviewModal && !!selectedBadge}
        onClose={() => setShowPreviewModal(false)}
        title="Badge Details"
        subtitle="Complete badge information"
        size="xl"
        maxHeight="90vh"
      >
        {selectedBadge && (() => {
          const badgeColor = (selectedBadge as any).icon_color || (selectedBadge as any).iconColor || '#6366f1';
          const badgeIconType = (selectedBadge as any).icon_type || (selectedBadge as any).iconType;
          const PreviewIcon = getIconComponent(badgeIconType);
          const badgeImageUrl = getBadgeImageUrl(selectedBadge);

          return (
            <div className="space-y-6">
              {/* Badge Hero Section */}
              <div
                className="relative rounded-2xl p-8 shadow-xl overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${badgeColor} 0%, ${badgeColor}dd 50%, ${badgeColor}99 100%)` }}
              >
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>

                <div className="relative text-center">
                  {/* Badge Icon/Image */}
                  <div className="inline-block mb-4">
                    {badgeImageUrl ? (
                      <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white/40 shadow-2xl mx-auto ring-4 ring-white/20">
                        <img src={badgeImageUrl} alt={selectedBadge.badge_name} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div
                        className="w-28 h-28 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl ring-4 ring-white/30 mx-auto transition-transform hover:scale-105"
                      >
                        <PreviewIcon className="w-14 h-14 text-white drop-shadow-lg" />
                      </div>
                    )}
                  </div>

                  {/* Badge Name & Description */}
                  <h4 className="text-3xl font-bold text-white mb-2 drop-shadow-lg">{selectedBadge.badge_name}</h4>
                  <p className="text-white/90 text-base mb-5 max-w-md mx-auto leading-relaxed">{selectedBadge.description}</p>

                  {/* Badge Tags */}
                  <div className="flex items-center justify-center gap-3 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold shadow-lg ${getRarityBadge(selectedBadge.rarity)} border-2`}>
                      <Star className="w-4 h-4" />
                      {selectedBadge.rarity?.charAt(0).toUpperCase() + selectedBadge.rarity?.slice(1)}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-white text-gray-800 shadow-lg">
                      <Zap className="w-4 h-4 text-amber-500" />
                      {getBadgeXP(selectedBadge).toLocaleString()} XP
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-white/20 backdrop-blur-sm text-white border border-white/30">
                      <Tag className="w-4 h-4" />
                      {selectedBadge.category}
                    </span>
                  </div>
                </div>
              </div>

              {/* Statistics Section */}
              <div>
                <h5 className="font-semibold text-gray-900 text-base mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  Badge Information
                </h5>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Status Card */}
                  <div className={`rounded-xl p-4 border-2 transition-all hover:shadow-md ${selectedBadge.is_active ? 'bg-gradient-to-br from-green-50 to-emerald-100 border-green-200' : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'}`}>
                    <div className="flex flex-col gap-2">
                      <div className={`p-2.5 rounded-xl w-fit ${selectedBadge.is_active ? 'bg-green-500' : 'bg-gray-500'}`}>
                        {selectedBadge.is_active ? <Unlock className="w-5 h-5 text-white" /> : <Lock className="w-5 h-5 text-white" />}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</p>
                        <p className={`text-xl font-bold ${selectedBadge.is_active ? 'text-green-700' : 'text-gray-700'}`}>
                          {selectedBadge.is_active ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* XP Card */}
                  <div className="rounded-xl p-4 border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-100 transition-all hover:shadow-md">
                    <div className="flex flex-col gap-2">
                      <div className="p-2.5 rounded-xl w-fit bg-amber-500">
                        <Zap className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">XP Required</p>
                        <p className="text-xl font-bold text-amber-700">{getBadgeXP(selectedBadge).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Rarity Card */}
                  <div className="rounded-xl p-4 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-100 transition-all hover:shadow-md">
                    <div className="flex flex-col gap-2">
                      <div className="p-2.5 rounded-xl w-fit bg-purple-500">
                        <Star className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Rarity</p>
                        <p className="text-xl font-bold text-purple-700 capitalize">{selectedBadge.rarity}</p>
                      </div>
                    </div>
                  </div>

                  {/* Created Card */}
                  <div className="rounded-xl p-4 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-100 transition-all hover:shadow-md">
                    <div className="flex flex-col gap-2">
                      <div className="p-2.5 rounded-xl w-fit bg-blue-500">
                        <Calendar className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</p>
                        <p className="text-lg font-bold text-blue-700">
                          {selectedBadge.createdAt ? new Date(selectedBadge.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    if (!canWrite) return;
                    setShowPreviewModal(false);
                    handleEdit(selectedBadge);
                  }}
                  disabled={!canWrite}
                  title={!canWrite ? 'You do not have permission' : undefined}
                  className={`flex-1 px-5 py-3 bg-indigo-600 text-white rounded-xl transition-all font-semibold flex items-center justify-center gap-2 shadow-lg ${
                    canWrite ? 'hover:bg-indigo-700 hover:shadow-xl' : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <Edit className="w-5 h-5" />
                  Edit Badge
                </button>
                <button
                  onClick={() => {
                    if (!canWrite) return;
                    setShowPreviewModal(false);
                    setShowDeleteModal(true);
                  }}
                  disabled={!canWrite}
                  title={!canWrite ? 'You do not have permission' : undefined}
                  className={`px-5 py-3 border-2 border-red-200 text-red-600 rounded-xl transition-all font-semibold flex items-center justify-center gap-2 ${
                    canWrite ? 'hover:bg-red-50' : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <Trash2 className="w-5 h-5" />
                  Delete
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}